import { useElementVisibility, watchOnce } from '@vueuse/core'
import {
  logAdClickToAY,
  logAdImpressionToAY,
} from '../../utils/ad-ops/assertive-yield/events'
import { useAnalytics } from '../analytics/useAnalytics'
import type { NtpAdEvent } from '../analytics/types'
import debug from '../../utils/console'
import { BREAKPOINTS } from '../../theme/common'
import { handleStickyAd } from '../../utils/ad-ops/adFunctions'
import { anyAppOwnedPlacement } from '../../utils/ad-ops/assertive-yield/slotTiming'
import type { AyLogImpressionEvent } from '../../types/assertive'

const logger = debug.withTag('useAdFunctions')

function getBreakpoint(width: number): string {
  if (width >= BREAKPOINTS.LG) return 'LG'
  if (width >= BREAKPOINTS.MD) return 'MD'
  if (width >= BREAKPOINTS.SM) return 'SM'
  return 'XS'
}

// Every ad component calls this composable, so the breakpoint watch and the
// pending post-navigation refresh are shared: one resize or tab click must
// produce one auction, not one per mounted ad.
let listeningForBreakpoints = false
let refreshPending = false
const PAGE_CHANGE_WAIT_MS = 30_000
const useAdFunctions = () => {
  const { track } = useAnalytics()
  const { $ayManagerEnv } = useNuxtApp()
  // The ad stack is browser only. The shared Taboola and Outbrain composables
  // inject their scripts when they are first created, so a server render gets
  // no-op renderers instead of creating them.
  const noRender = async (): Promise<void> => {}
  const { callRenderResearchWidgets } = import.meta.client
    ? useOutbrainAds()
    : { callRenderResearchWidgets: noRender }
  const { callRenderTaboolaWidgets } = import.meta.client
    ? useTaboolaAds()
    : { callRenderTaboolaWidgets: noRender }

  const adClicked = (eventData: NtpAdEvent, slotId: string) => {
    logAdClickToAY(slotId)
    track('NtpAd_Clicked', eventData)
  }

  const adRendered = (
    eventData: NtpAdEvent,
    ayData: Partial<AyLogImpressionEvent>
  ) => {
    logAdImpressionToAY(ayData)
    track('NtpAd_Rendered', eventData)
  }

  /**
   * Renders content when an element becomes visible in the viewport
   * @param element - The HTML element to monitor for visibility
   * @param lazyRender - If true, the element will only be rendered when it becomes visible
   * visibilityTimeoutInMs - Delay in milliseconds before executing callback after visibility
   * AND visibilityThresholdInPercentage - Percentage of element that must be visible (default: 10%)
   * @param callbackFn - Function to execute when element becomes visible (can be sync or async)
   */
  const renderWhenCurrentElementIsVisible = (
    element: HTMLElement | null,
    lazyRender: boolean,
    sticky: boolean,
    callbackFn: () => void | Promise<void>
  ) => {
    let visibilityTimer: ReturnType<typeof setTimeout> | number = 0

    try {
      if (!element) {
        throw new Error('No element to render when visible')
      }

      if (!lazyRender) {
        if (sticky) handleStickyAd(element)

        // Handle both sync and async callbacks
        const result = callbackFn()
        if (result instanceof Promise) {
          result.catch((err) => {
            logger.error('Error in async callback for non-lazy render', err)
          })
        }
        return
      }

      const isVisible = useElementVisibility(element, {
        threshold: 1 / 100, //visibilityThresholdInPercentage
      })

      watchOnce(isVisible, () => {
        if (visibilityTimer) {
          clearTimeout(visibilityTimer)
        }

        if (isVisible.value) {
          visibilityTimer = setTimeout(() => {
            if (sticky) handleStickyAd(element)

            // Handle both sync and async callbacks
            const result = callbackFn()
            if (result instanceof Promise) {
              result.catch((err) => {
                logger.error(
                  'Error in async callback for element visibility',
                  err
                )
              })
            }
          }, 1) // visibilityTimeoutInMs
        }
      })
    } catch (err) {
      if (visibilityTimer) {
        clearTimeout(visibilityTimer)
      }
      logger.error('Unable to render element when visible', err)
    }
  }

  // Re-render the ad units for the current layout/category via the manual-event
  // dispatch WITHOUT calling changePage(), so it never counts an AY pageview.
  // Used for layout/breakpoint changes and category-tab clicks; accepted
  // NtpPage_Viewed events own pageview changes.
  const refreshAyAuction = () => {
    $ayManagerEnv?.cmd?.push(function () {
      const env = $ayManagerEnv
      if (!env) return
      // An app-owned placement renders on the manual event, which stays fired
      // once dispatched: every slot the app is holding would render at once,
      // off screen, and every later one as soon as its auction ends.
      if (anyAppOwnedPlacement(env)) return
      env.dispatchManualEvent()
    })
  }

  const renderNativeWidgets = () => {
    callRenderResearchWidgets()
    callRenderTaboolaWidgets()
  }

  const refreshAdUnits = () => {
    try {
      refreshAyAuction()
      renderNativeWidgets()
    } catch (err) {
      logger.error('Error refreshing ad units', err)
    }
  }

  // Only the AY auction waits for AY's page change: refreshing first would
  // re-auction the previous category's instances against the previous
  // pageview. Native widgets don't depend on AY, so a blocked AY can't hold
  // them back.
  const refreshAdUnitsAfterPageChange = () => {
    try {
      renderNativeWidgets()
    } catch (err) {
      logger.error('Error refreshing native widgets', err)
    }
    if (refreshPending || !$ayManagerEnv?.cmd) return
    refreshPending = true
    const env = $ayManagerEnv
    // Armed outside the command queue, which never runs while AY is blocked.
    // A rejected or deduplicated pageview never changes page either; drop the
    // refresh rather than fire it on some later, unrelated page change.
    const giveUp = setTimeout(() => {
      refreshPending = false
      env.cmd.push(() => env.offEvent('afterPageChange', onAfterPageChange))
    }, PAGE_CHANGE_WAIT_MS)
    function onAfterPageChange() {
      clearTimeout(giveUp)
      env.offEvent('afterPageChange', onAfterPageChange)
      refreshPending = false
      refreshAyAuction()
    }
    env.cmd.push(() => env.onEvent('afterPageChange', onAfterPageChange))
  }

  const { width } = useWindowSize()

  const isMobile = computed(() => {
    return Boolean(width.value <= BREAKPOINTS.LG)
  })

  const isXLDesktop = computed(() => {
    return Boolean(width.value >= BREAKPOINTS.XL)
  })

  // A plain window listener: it must outlive whichever component called first.
  if (import.meta.client && !listeningForBreakpoints) {
    listeningForBreakpoints = true
    let currentBreakpoint = getBreakpoint(window.innerWidth)
    window.addEventListener(
      'resize',
      () => {
        const newBreakpoint = getBreakpoint(window.innerWidth)
        if (newBreakpoint === currentBreakpoint) return
        currentBreakpoint = newBreakpoint
        // A breakpoint/resize is not a navigation. Re-render the units
        // for the new layout WITHOUT counting a pageview.
        refreshAdUnits()
      },
      { passive: true }
    )
  }

  return {
    adClicked,
    adRendered,
    renderWhenCurrentElementIsVisible,
    refreshAdUnits,
    refreshAdUnitsAfterPageChange,
    isMobile,
    isXLDesktop,
  }
}

export default useAdFunctions
